import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsApi, resolveReportsApiError } from '../services/reportsApi';
import {
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from '../components/commissions/utils';
import './ReportsPage.css';

const REPORT_TYPES = [
  { value: 'collections', label: 'Collections Report' },
  { value: 'commissions', label: 'Commission Report' },
  { value: 'payment_requests', label: 'Payment Requests Report' },
  { value: 'liquidations', label: 'Liquidation Report' },
  { value: 'replenishments', label: 'Replenishment Report' },
];

const STATUS_OPTIONS = {
  collections: ['Pending', 'Approved', 'Paid', 'Cancelled'],
  commissions: ['Pending', 'Released'],
  payment_requests: ['Pending', 'Approved', 'Rejected', 'Released', 'For Liquidation', 'Liquidated'],
  liquidations: ['Draft', 'Submitted', 'Audited', 'Approved'],
  replenishments: ['Draft', 'Submitted', 'Verified', 'Approved'],
};

const POSITION_OPTIONS = ['Sales Advisor', 'Unit Manager', 'Branch Manager', 'Assistant Area Head', 'Area Head', 'Senior Area Head', 'Division Head', 'CEO'];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return `PHP ${toNumber(value).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function dateMatchesRange(value, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!value) {
    return false;
  }

  const rowDate = new Date(value);
  if (Number.isNaN(rowDate.getTime())) {
    return false;
  }

  const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
  const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

  if (from && rowDate < from) {
    return false;
  }
  if (to && rowDate > to) {
    return false;
  }

  return true;
}

function resolveRoleLabel(role) {
  const normalized = String(role || '').trim().toUpperCase();
  const labels = {
    SA: 'Sales Advisor',
    UM: 'Unit Manager',
    BM: 'Branch Manager',
    AAH: 'Assistant Area Head',
    DH: 'Division Head',
    AH: 'Area Head',
    SAH: 'Senior Area Head',
    CEO: 'CEO',
  };

  return labels[normalized] || String(role || '');
}

function resolvePositionField(position) {
  const map = {
    SA: 'sa_commission',
    UM: 'um_commission',
    BM: 'bm_commission',
    AAH: 'aah_commission',
    DH: 'dh_commission',
    AH: 'ah_commission',
    SAH: 'sah_commission',
    CEO: 'ceo_commission',
    'Sales Advisor': 'sa_commission',
    'Unit Manager': 'um_commission',
    'Branch Manager': 'bm_commission',
    'Assistant Area Head': 'aah_commission',
    'Division Head': 'dh_commission',
    'Area Head': 'ah_commission',
    'Senior Area Head': 'sah_commission',
  };

  const normalized = String(position || '').trim();
  if (!normalized) {
    return null;
  }
  if (normalized === 'CEO') {
    return 'ceo_commission';
  }

  return map[normalized] || map[normalized.toUpperCase()] || null;
}

// Converts a product-setup percent value (0-100, so 1 means 1%) to a fraction.
// Always divides by 100 — the old "> 1" heuristic misread 1 as 100%. Do NOT call
// this on collections.com_rate, which is already stored as a fraction.
function normalizeRate(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }

  return parsed / 100;
}

function getTodayToken() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

function computeCommissionValues(row, rateInput) {
  const premium = toNumber(row.premium_amount);
  const afterVat = row.after_vat !== null && row.after_vat !== undefined
    ? toNumber(row.after_vat)
    : (premium > 0 ? premium / 1.12 : 0);
  // rateInput (row._rate from getCommissionRate) is already a fraction —
  // re-normalizing here would divide it by 100 a second time.
  const rate = Math.max(toNumber(rateInput), 0);
  const gross = afterVat * rate;
  const wtax = gross * 0.1;
  const net = gross - wtax;

  return {
    premium,
    afterVat,
    rate,
    gross,
    wtax,
    net,
  };
}

export default function ReportsPage() {
  const [reportType, setReportType] = useState('collections');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [search, setSearch] = useState('');

  const [access, setAccess] = useState({
    is_admin: false,
    role: 'user',
    branch_id: null,
    branch: null,
    scopes: [],
  });
  const [collections, setCollections] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [replenishments, setReplenishments] = useState([]);
  const [sales, setSales] = useState([]);
  const [agents, setAgents] = useState([]);
  const [productCommissions, setProductCommissions] = useState([]);
  const [branches, setBranches] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await reportsApi.listReports({ bustCache: true });
      const payload = response?.data || {};
      setAccess(payload.access || {
        is_admin: false,
        role: 'user',
        branch_id: null,
        branch: null,
        scopes: [],
      });
      setCollections(Array.isArray(payload.collections) ? payload.collections : []);
      setPaymentRequests(Array.isArray(payload.payment_requests) ? payload.payment_requests : []);
      setLiquidations(Array.isArray(payload.liquidations) ? payload.liquidations : []);
      setReplenishments(Array.isArray(payload.replenishments) ? payload.replenishments : []);
      setSales(Array.isArray(payload.sales) ? payload.sales : []);
      setAgents(Array.isArray(payload.agents) ? payload.agents : []);
      setProductCommissions(Array.isArray(payload.product_commissions) ? payload.product_commissions : []);
      setBranches(Array.isArray(payload.branches) ? payload.branches : []);
    } catch (err) {
      setError(resolveReportsApiError(err));
      setCollections([]);
      setPaymentRequests([]);
      setLiquidations([]);
      setReplenishments([]);
      setSales([]);
      setAgents([]);
      setProductCommissions([]);
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isAdmin = Boolean(access?.is_admin);
  const activeBranch = isAdmin
    ? filterBranch
    : (String(access?.branch || '').trim() || 'all');

  const salesById = useMemo(() => {
    const map = new Map();
    sales.forEach((sale) => {
      map.set(Number(sale.id), sale);
    });
    return map;
  }, [sales]);

  const productByName = useMemo(() => {
    const map = new Map();
    productCommissions.forEach((item) => {
      const key = normalizeText(item.product_name);
      if (!key) {
        return;
      }
      map.set(key, item);
    });
    return map;
  }, [productCommissions]);

  const getCommissionRate = useCallback((productName, positionLabel) => {
    const key = normalizeText(productName);
    if (!key || !productByName.has(key)) {
      return 0;
    }

    const product = productByName.get(key);
    const field = resolvePositionField(positionLabel);
    if (!field) {
      return 0;
    }

    return normalizeRate(product?.[field]);
  }, [productByName]);

  const branchOptions = useMemo(() => {
    const names = new Set();
    branches.forEach((row) => {
      const name = String(row?.name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    [collections, paymentRequests, liquidations, replenishments].forEach((group) => {
      group.forEach((row) => {
        const name = String(row?.branch || row?.branch_name || '').trim();
        if (name) {
          names.add(name);
        }
      });
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [branches, collections, paymentRequests, liquidations, replenishments]);

  const productOptions = useMemo(() => {
    const names = new Set();
    productCommissions.forEach((row) => {
      const name = String(row?.product_name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    collections.forEach((row) => {
      const name = String(row?.product || '').trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [collections, productCommissions]);

  const agentOptions = useMemo(() => {
    const names = new Set();
    agents.forEach((row) => {
      const name = String(row?.full_name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    collections.forEach((row) => {
      const name = String(row?.agent_name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [agents, collections]);

  const matchesCommonFilters = useCallback((row, dateValue, searchableParts = []) => {
    if (!dateMatchesRange(dateValue, dateFrom, dateTo)) {
      return false;
    }

    if (activeBranch !== 'all') {
      const rowBranch = String(row?.branch || row?.branch_name || '').trim();
      if (rowBranch !== activeBranch) {
        return false;
      }
    }

    const searchKey = normalizeText(search);
    if (!searchKey) {
      return true;
    }

    const blob = searchableParts
      .map((value) => normalizeText(value))
      .join(' ');

    return blob.includes(searchKey);
  }, [activeBranch, dateFrom, dateTo, search]);

  const filteredData = useMemo(() => {
    if (reportType === 'collections') {
      return collections.filter((row) => {
        if (!matchesCommonFilters(row, row.payment_date || row.date, [
          row.transaction_no,
          row.ar_si_no,
          row.agent_name,
          row.agent_position,
          row.client_name,
          row.product,
          row.branch,
          row.status,
        ])) {
          return false;
        }
        if (filterProduct !== 'all' && String(row.product || '') !== filterProduct) {
          return false;
        }
        if (filterPosition !== 'all' && String(row.agent_position || '') !== filterPosition) {
          return false;
        }
        if (filterAgent !== 'all' && String(row.agent_name || '') !== filterAgent) {
          return false;
        }
        if (filterStatus !== 'all' && String(row.status || '') !== filterStatus) {
          return false;
        }
        return true;
      });
    }

    if (reportType === 'commissions') {
      const paidRows = collections.filter((row) => {
        if (String(row.status || '') !== 'Paid') {
          return false;
        }
        if (toNumber(row.com_base) <= 0) {
          return false;
        }
        if (!matchesCommonFilters(row, row.payment_date || row.date, [
          row.transaction_no,
          row.ar_si_no,
          row.agent_name,
          row.agent_position,
          row.client_name,
          row.product,
          row.branch,
          row.commission_status,
          row.agent_commission_status,
        ])) {
          return false;
        }
        if (filterProduct !== 'all' && String(row.product || '') !== filterProduct) {
          return false;
        }
        return true;
      });

      const expanded = paidRows.flatMap((row) => {
        const sale = salesById.get(Number(row.sale_id || 0));
        const commissionAgents = Array.isArray(sale?.commission_agents) ? sale.commission_agents : [];

        if (commissionAgents.length === 0) {
          const positionLabel = String(row.agent_position || resolveRoleLabel(row.commission_agent_role || ''));
          return [{
            ...row,
            row_key: `collection-${row.id}-base`,
            _autodeduct: false,
            _rate: getCommissionRate(row.product, positionLabel),
            _position_label: positionLabel,
            _status: String(row.agent_commission_status || row.commission_status || 'Pending'),
          }];
        }

        return commissionAgents.map((commissionAgent, index) => {
          const positionLabel = resolveRoleLabel(commissionAgent.role || '') || row.agent_position || '';
          return {
            ...row,
            row_key: `collection-${row.id}-${commissionAgent.id || commissionAgent.role || index}`,
            agent_name: commissionAgent.agent_name || row.agent_name,
            agent_position: positionLabel,
            _position_label: positionLabel,
            _autodeduct: Boolean(commissionAgent.autodeduct),
            _rate: getCommissionRate(row.product, positionLabel),
            _status: String(row.agent_commission_status || row.commission_status || 'Pending'),
          };
        });
      });

      return expanded.filter((row) => {
        if (filterPosition !== 'all' && String(row.agent_position || '') !== filterPosition) {
          return false;
        }
        if (filterAgent !== 'all' && String(row.agent_name || '') !== filterAgent) {
          return false;
        }
        if (filterStatus !== 'all' && String(row._status || '') !== filterStatus) {
          return false;
        }
        return true;
      });
    }

    if (reportType === 'payment_requests') {
      return paymentRequests.filter((row) => {
        if (!matchesCommonFilters(row, row.date, [
          row.transaction_no,
          row.requestor_name,
          row.request_type,
          row.branch,
          row.branch_name,
          row.status,
          row.release_reference,
        ])) {
          return false;
        }
        if (filterStatus !== 'all' && String(row.status || '') !== filterStatus) {
          return false;
        }
        return true;
      });
    }

    if (reportType === 'liquidations') {
      return liquidations.filter((row) => {
        if (!matchesCommonFilters(row, row.date, [
          row.liquidation_no,
          row.payment_request_no,
          row.voucher_no,
          row.purpose,
          row.branch,
          row.status,
        ])) {
          return false;
        }
        if (filterStatus !== 'all' && String(row.status || '') !== filterStatus) {
          return false;
        }
        return true;
      });
    }

    return replenishments.filter((row) => {
      if (!matchesCommonFilters(row, row.period_start, [
        row.custodian,
        row.branch,
        row.status,
      ])) {
        return false;
      }
      if (filterStatus !== 'all' && String(row.status || '') !== filterStatus) {
        return false;
      }
      return true;
    });
  }, [
    reportType,
    collections,
    paymentRequests,
    liquidations,
    replenishments,
    salesById,
    matchesCommonFilters,
    filterProduct,
    filterPosition,
    filterStatus,
    filterAgent,
    getCommissionRate,
  ]);

  const summary = useMemo(() => {
    if (reportType === 'collections') {
      return {
        totalPremium: filteredData.reduce((sum, row) => sum + toNumber(row.premium_amount), 0),
        totalGross: filteredData.reduce((sum, row) => sum + toNumber(row.gross_amount), 0),
        totalWtax: filteredData.reduce((sum, row) => sum + toNumber(row.wtax), 0),
        totalNet: filteredData.reduce((sum, row) => sum + toNumber(row.net_commission), 0),
      };
    }

    if (reportType === 'commissions') {
      return filteredData.reduce((acc, row) => {
        const values = computeCommissionValues(row, row._rate || 0);
        return {
          totalPremium: acc.totalPremium + values.premium,
          totalGross: acc.totalGross + values.gross,
          totalWtax: acc.totalWtax + values.wtax,
          totalNet: acc.totalNet + values.net,
        };
      }, {
        totalPremium: 0,
        totalGross: 0,
        totalWtax: 0,
        totalNet: 0,
      });
    }

    if (reportType === 'payment_requests') {
      return {
        totalAmount: filteredData.reduce((sum, row) => sum + toNumber(row.total_amount), 0),
      };
    }

    if (reportType === 'liquidations') {
      return {
        totalSpent: filteredData.reduce((sum, row) => sum + toNumber(row.total_spent), 0),
        totalAdvance: filteredData.reduce((sum, row) => sum + toNumber(row.cash_advance_amount), 0),
      };
    }

    return {
      totalReplenishment: filteredData.reduce((sum, row) => {
        const itemsTotal = Array.isArray(row.items)
          ? row.items.reduce((itemSum, item) => itemSum + toNumber(item.amount), 0)
          : 0;
        return sum + itemsTotal;
      }, 0),
    };
  }, [filteredData, reportType]);

  function clearFilters() {
    setDateFrom('');
    setDateTo('');
    setFilterProduct('all');
    setFilterPosition('all');
    setFilterStatus('all');
    setFilterAgent('all');
    setFilterBranch('all');
    setSearch('');
  }

  function buildExport() {
    const exportLabel = REPORT_TYPES.find((item) => item.value === reportType)?.label || 'Report';
    const dateToken = getTodayToken();

    if (reportType === 'collections') {
      const headers = [
        'Payment Date',
        'AR/SI No.',
        'Agent',
        'Position',
        'Client',
        'Product',
        'Branch',
        'Premium Amount',
        'After VAT',
        'NOP',
        'Com Rate',
        'Gross Commission',
        'W/Tax',
        'Net Commission',
        'Status',
      ];
      const rows = filteredData.map((row) => ([
        formatDate(row.payment_date || row.date),
        row.ar_si_no || '',
        row.agent_name || '',
        row.agent_position || '',
        row.client_name || '',
        row.product || '',
        row.branch || '',
        toNumber(row.premium_amount).toFixed(2),
        toNumber(row.after_vat).toFixed(2),
        toNumber(row.nop).toFixed(0),
        `${(toNumber(row.com_rate) * 100).toFixed(2)}%`,
        toNumber(row.gross_amount).toFixed(2),
        toNumber(row.wtax).toFixed(2),
        toNumber(row.net_commission).toFixed(2),
        row.status || '',
      ]));

      return { label: exportLabel, token: dateToken, headers, rows };
    }

    if (reportType === 'commissions') {
      const headers = [
        'Payment Date',
        'Agent',
        'Position',
        'Auto-Deduct',
        'Client',
        'Product',
        'Branch',
        'Premium',
        'After VAT',
        'Com Rate',
        'Gross Commission',
        'W/Tax',
        'Net Commission',
        'Commission Status',
      ];
      const rows = filteredData.map((row) => {
        const values = computeCommissionValues(row, row._rate || 0);
        return [
          formatDate(row.payment_date || row.date),
          row.agent_name || '',
          row.agent_position || '',
          row._autodeduct ? 'Yes' : 'No',
          row.client_name || '',
          row.product || '',
          row.branch || '',
          values.premium.toFixed(2),
          values.afterVat.toFixed(2),
          `${(values.rate * 100).toFixed(2)}%`,
          values.gross.toFixed(2),
          values.wtax.toFixed(2),
          values.net.toFixed(2),
          row._status || 'Pending',
        ];
      });

      return { label: exportLabel, token: dateToken, headers, rows };
    }

    if (reportType === 'payment_requests') {
      const headers = ['Date', 'Transaction No.', 'Requestor', 'Type', 'Branch', 'Total Amount', 'Status'];
      const rows = filteredData.map((row) => ([
        formatDate(row.date),
        row.transaction_no || '',
        row.requestor_name || '',
        row.request_type || '',
        row.branch || row.branch_name || '',
        toNumber(row.total_amount).toFixed(2),
        row.status || '',
      ]));

      return { label: exportLabel, token: dateToken, headers, rows };
    }

    if (reportType === 'liquidations') {
      const headers = [
        'Liquidation No.',
        'Date',
        'Branch',
        'Voucher No.',
        'Purpose',
        'Total Spent',
        'Cash Advance',
        'Excess',
        'Status',
      ];
      const rows = filteredData.map((row) => ([
        row.liquidation_no || '',
        formatDate(row.date),
        row.branch || '',
        row.voucher_no || '',
        row.purpose || '',
        toNumber(row.total_spent).toFixed(2),
        toNumber(row.cash_advance_amount).toFixed(2),
        toNumber(row.excess).toFixed(2),
        row.status || '',
      ]));

      return { label: exportLabel, token: dateToken, headers, rows };
    }

    const headers = ['Custodian', 'Period Start', 'Period End', 'Branch', 'Total Replenishment', 'Revolving Fund', 'Status'];
    const rows = filteredData.map((row) => {
      const totalItems = Array.isArray(row.items)
        ? row.items.reduce((sum, item) => sum + toNumber(item.amount), 0)
        : 0;
      return [
        row.custodian || '',
        formatDate(row.period_start),
        formatDate(row.period_end),
        row.branch || '',
        totalItems.toFixed(2),
        toNumber(row.revolving_fund).toFixed(2),
        row.status || '',
      ];
    });

    return { label: exportLabel, token: dateToken, headers, rows };
  }

  function exportCsv() {
    const file = buildExport();
    if (file.rows.length === 0) {
      return;
    }
    triggerCsvDownload(`${file.label.replace(/\s+/g, '_')}_${file.token}.csv`, file.headers, file.rows);
  }

  function exportExcel() {
    const file = buildExport();
    if (file.rows.length === 0) {
      return;
    }
    triggerExcelLikeDownload(`${file.label.replace(/\s+/g, '_')}_${file.token}.xls`, file.headers, file.rows);
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Reports</h1>
        <p>{isAdmin ? 'Generate and export multi-module reports across all branches.' : `Branch: ${access.branch || 'Unassigned'}`}</p>
      </section>

      <section className="dashboard-content reports-page-content">
        <div className="reports-type-tabs">
          {REPORT_TYPES.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`reports-type-btn ${reportType === item.value ? 'is-active' : ''}`}
              onClick={() => {
                setReportType(item.value);
                clearFilters();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="reports-toolbar">
          <div className="reports-filters-grid">
            {isAdmin ? (
              <div className="reports-field">
                <label>Branch</label>
                <select value={filterBranch} onChange={(event) => setFilterBranch(event.target.value)}>
                  <option value="all">All Branches</option>
                  {branchOptions.map((branchName) => (
                    <option key={`branch-${branchName}`} value={branchName}>
                      {branchName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="reports-field">
                <label>Branch</label>
                <input type="text" value={access.branch || 'Unassigned'} disabled />
              </div>
            )}

            <div className="reports-field">
              <label>Date From</label>
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </div>

            <div className="reports-field">
              <label>Date To</label>
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </div>

            {(reportType === 'collections' || reportType === 'commissions') ? (
              <>
                <div className="reports-field">
                  <label>Product</label>
                  <select value={filterProduct} onChange={(event) => setFilterProduct(event.target.value)}>
                    <option value="all">All Products</option>
                    {productOptions.map((name) => (
                      <option key={`product-${name}`} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                <div className="reports-field">
                  <label>Position</label>
                  <select value={filterPosition} onChange={(event) => setFilterPosition(event.target.value)}>
                    <option value="all">All Positions</option>
                    {POSITION_OPTIONS.map((name) => (
                      <option key={`position-${name}`} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                <div className="reports-field">
                  <label>Agent</label>
                  <select value={filterAgent} onChange={(event) => setFilterAgent(event.target.value)}>
                    <option value="all">All Agents</option>
                    {agentOptions.map((name) => (
                      <option key={`agent-${name}`} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}

            {STATUS_OPTIONS[reportType]?.length > 0 ? (
              <div className="reports-field">
                <label>Status</label>
                <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
                  <option value="all">All Status</option>
                  {STATUS_OPTIONS[reportType].map((status) => (
                    <option key={`status-${status}`} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="reports-field reports-field-wide">
              <label>Search</label>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search record details..."
              />
            </div>
          </div>

          <div className="reports-toolbar-actions">
            <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={clearFilters}>
              Clear Filters
            </button>
            <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        {error ? <div className="error-box">{error}</div> : null}

        {(reportType === 'collections' || reportType === 'commissions') ? (
          <div className="reports-summary-grid">
            <div className="reports-summary-card"><p>Total Premium</p><strong>{formatCurrency(summary.totalPremium)}</strong></div>
            <div className="reports-summary-card"><p>Gross Commission</p><strong>{formatCurrency(summary.totalGross)}</strong></div>
            <div className="reports-summary-card"><p>W/Tax</p><strong>{formatCurrency(summary.totalWtax)}</strong></div>
            <div className="reports-summary-card"><p>Net Commission</p><strong>{formatCurrency(summary.totalNet)}</strong></div>
          </div>
        ) : null}

        {reportType === 'payment_requests' ? (
          <div className="reports-summary-grid reports-summary-grid-single">
            <div className="reports-summary-card"><p>Total Requested</p><strong>{formatCurrency(summary.totalAmount)}</strong></div>
          </div>
        ) : null}

        {reportType === 'liquidations' ? (
          <div className="reports-summary-grid">
            <div className="reports-summary-card"><p>Total Spent</p><strong>{formatCurrency(summary.totalSpent)}</strong></div>
            <div className="reports-summary-card"><p>Total Advance</p><strong>{formatCurrency(summary.totalAdvance)}</strong></div>
            <div className="reports-summary-card"><p>Total Excess</p><strong>{formatCurrency(toNumber(summary.totalAdvance) - toNumber(summary.totalSpent))}</strong></div>
          </div>
        ) : null}

        {reportType === 'replenishments' ? (
          <div className="reports-summary-grid reports-summary-grid-single">
            <div className="reports-summary-card"><p>Total Replenishment</p><strong>{formatCurrency(summary.totalReplenishment)}</strong></div>
          </div>
        ) : null}

        {isLoading ? (
          <div className="dashboard-loading">Loading reports...</div>
        ) : filteredData.length === 0 ? (
          <div className="dashboard-empty">No records match the selected filters.</div>
        ) : (
          <div className="dashboard-table-wrap">
            {(reportType === 'collections' || reportType === 'commissions') ? (
              <table className="dashboard-table reports-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Agent</th>
                    <th>Position</th>
                    <th>Client</th>
                    <th>Product</th>
                    <th>Branch</th>
                    <th>Premium</th>
                    <th>After VAT</th>
                    <th>NOP</th>
                    <th>Com Rate</th>
                    <th>Gross</th>
                    <th>W/Tax</th>
                    <th>Net Com</th>
                    {reportType === 'collections' ? <th>Status</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => {
                    const values = reportType === 'commissions'
                      ? computeCommissionValues(row, row._rate || 0)
                      : {
                        premium: toNumber(row.premium_amount),
                        afterVat: toNumber(row.after_vat),
                        rate: toNumber(row.com_rate),
                        gross: toNumber(row.gross_amount),
                        wtax: toNumber(row.wtax),
                        net: toNumber(row.net_commission),
                      };

                    return (
                      <tr key={row.row_key || row.id}>
                        <td>{formatDate(row.payment_date || row.date)}</td>
                        <td>{row.agent_name || '-'}</td>
                        <td>{row.agent_position || '-'}</td>
                        <td>{row.client_name || '-'}</td>
                        <td>{row.product || '-'}</td>
                        <td>{row.branch || '-'}</td>
                        <td className="reports-number-cell">{formatCurrency(values.premium)}</td>
                        <td className="reports-number-cell">{formatCurrency(values.afterVat)}</td>
                        <td className="reports-number-cell">{toNumber(row.nop).toLocaleString('en-PH')}</td>
                        <td className="reports-number-cell">{`${(values.rate * 100).toFixed(2)}%`}</td>
                        <td className="reports-number-cell">{formatCurrency(values.gross)}</td>
                        <td className="reports-number-cell">{formatCurrency(values.wtax)}</td>
                        <td className="reports-number-cell">{formatCurrency(values.net)}</td>
                        {reportType === 'collections' ? (
                          <td>
                            <span className={`status-badge status-${normalizeText(row.status)}`}>
                              {row.status || '-'}
                            </span>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}

            {reportType === 'payment_requests' ? (
              <table className="dashboard-table reports-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Transaction No.</th>
                    <th>Requestor</th>
                    <th>Type</th>
                    <th>Branch</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.date)}</td>
                      <td>{row.transaction_no || '-'}</td>
                      <td>{row.requestor_name || '-'}</td>
                      <td>{row.request_type || '-'}</td>
                      <td>{row.branch || row.branch_name || '-'}</td>
                      <td className="reports-number-cell">{formatCurrency(row.total_amount)}</td>
                      <td>
                        <span className={`status-badge status-${normalizeText(row.status)}`}>
                          {row.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {reportType === 'liquidations' ? (
              <table className="dashboard-table reports-table">
                <thead>
                  <tr>
                    <th>Liquidation No.</th>
                    <th>Date</th>
                    <th>Branch</th>
                    <th>Voucher No.</th>
                    <th>Purpose</th>
                    <th>Total Spent</th>
                    <th>Cash Advance</th>
                    <th>Excess</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.liquidation_no || '-'}</td>
                      <td>{formatDate(row.date)}</td>
                      <td>{row.branch || '-'}</td>
                      <td>{row.voucher_no || '-'}</td>
                      <td>{row.purpose || '-'}</td>
                      <td className="reports-number-cell">{formatCurrency(row.total_spent)}</td>
                      <td className="reports-number-cell">{formatCurrency(row.cash_advance_amount)}</td>
                      <td className="reports-number-cell">{formatCurrency(row.excess)}</td>
                      <td>
                        <span className={`status-badge status-${normalizeText(row.status)}`}>
                          {row.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {reportType === 'replenishments' ? (
              <table className="dashboard-table reports-table">
                <thead>
                  <tr>
                    <th>Custodian</th>
                    <th>Period Start</th>
                    <th>Period End</th>
                    <th>Branch</th>
                    <th>Total Replenishment</th>
                    <th>Revolving Fund</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => {
                    const totalItems = Array.isArray(row.items)
                      ? row.items.reduce((sum, item) => sum + toNumber(item.amount), 0)
                      : 0;

                    return (
                      <tr key={row.id}>
                        <td>{row.custodian || '-'}</td>
                        <td>{formatDate(row.period_start)}</td>
                        <td>{formatDate(row.period_end)}</td>
                        <td>{row.branch || '-'}</td>
                        <td className="reports-number-cell">{formatCurrency(totalItems)}</td>
                        <td className="reports-number-cell">{formatCurrency(row.revolving_fund)}</td>
                        <td>
                          <span className={`status-badge status-${normalizeText(row.status)}`}>
                            {row.status || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
